import { useCallback, useEffect, useState } from 'react'
import { get } from './lib/api'
import { me, signOut, type User } from './lib/auth'
import { t } from './lib/i18n'
import SignIn from './components/SignIn'
import Belt from './components/Belt'
import Voltron from './components/Voltron'
import Takeaways from './components/Takeaways'
import Log from './components/Log'
import Account from './components/Account'
import Admin from './components/Admin'
import EnvBanner from './components/EnvBanner'
import UpdateBar from './components/UpdateBar'
import InstallPrompt from './components/InstallPrompt'
import DebugPanel from './components/DebugPanel'
import ErrorBoundary from './components/ErrorBoundary'

export interface Health {
  app: string
  environment: 'local' | 'beta' | 'production'
  version: string
  commit: string
}

type Tab = 'home' | 'voltron' | 'takeaways' | 'log' | 'account' | 'admin'

export default function App() {
  const [user, setUser] = useState<User | null>(null)
  const [health, setHealth] = useState<Health | null>(null)
  const [ready, setReady] = useState(false)
  const [tab, setTab] = useState<Tab>('home')

  const refresh = useCallback(async () => {
    setUser(await me())
  }, [])

  useEffect(() => {
    void (async () => {
      // Both in parallel: neither depends on the other, and the boot is the one
      // moment the user is definitely waiting.
      const [u, h] = await Promise.all([
        me(),
        get<Health>('/api/health').catch(() => null),
      ])
      setUser(u)
      setHealth(h)
      setReady(true)
    })()
  }, [])

  if (!ready) return <div className="boot">{t('app.loading')}</div>

  return (
    <ErrorBoundary>
      {/* Above everything: an app running old code is the cause of bug reports
          nobody can reproduce. */}
      <UpdateBar />
      <EnvBanner health={health} />

      <header className="bar">
        <strong>{health?.app ?? 'Studio42 Factory'}</strong>
        {user && (
          <nav>
            <button onClick={() => setTab('home')} aria-current={tab === 'home'}>Belt</button>
            <button onClick={() => setTab('voltron')} aria-current={tab === 'voltron'}>Voltron</button>
            <button onClick={() => setTab('takeaways')} aria-current={tab === 'takeaways'}>Takeaways</button>
            <button onClick={() => setTab('log')} aria-current={tab === 'log'}>Log</button>
            <button onClick={() => setTab('account')} aria-current={tab === 'account'}>Account</button>
            {/* Capability-driven, so the tab is absent rather than present-and-denied. */}
            {user.capabilities.includes('admin.access') && (
              <button onClick={() => setTab('admin')} aria-current={tab === 'admin'}>{t('admin.title')}</button>
            )}
            <button onClick={async () => { await signOut(); await refresh() }}>{t('app.signOut')}</button>
          </nav>
        )}
      </header>

      <main>
        {!user && <SignIn onSignedIn={refresh} />}
        {user && tab === 'home' && <Belt canWrite={user.capabilities.includes('settings.write')} />}
        {user && tab === 'voltron' && <Voltron />}
        {user && tab === 'takeaways' && <Takeaways />}
        {user && tab === 'log' && <Log />}
        {user && tab === 'account' && <Account user={user} />}
        {user && tab === 'admin' && <Admin />}
      </main>

      {/* Capability, not environment: the phone that needs diagnosing is always
          a real one in production. */}
      {user?.capabilities.includes('errors.read') && <DebugPanel environment={health?.environment ?? '—'} />}

      {/* Below the fold, not in the way. For an app not on a store yet. */}
      <InstallPrompt />

      <footer className="muted small">
        {health ? `${health.version} · ${health.commit} · ${health.environment}` : ''}
      </footer>
    </ErrorBoundary>
  )
}
